import { FC, useCallback, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Stack, Typography } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import isNil from 'lodash.isnil'
import { GroupBase } from 'react-select'

import {
  SidePanelHeader,
  SidePanelObject,
  SidePanelElementProps,
  useSidePanelContext,
} from '@clara/layout-next'
import { Button, Select } from '@clara/ui'
import { Translation, useT } from '@clara/locale'
import { isEmpty } from '@clara/utils'
import { AsyncState } from '@clara/hooks'

import { getErrorHelperText } from '@/utils/form'
import { useSnackbar } from '@/utils/use-snackbar'
import { CpfMask } from '@/utils/string'

import { getBankInfoSchema } from '../use-bankinfo-schema'
import { BankName, BankNameResponse } from '../../../api/get-bank-name'
import {
  BankAccountType,
  BankInfoResponse,
  postBankInfo,
  TedPayload,
} from '../../../api/bank-info'
import { PatternField } from './pattern-field'
import { NumericField } from './numeric-field'
import { getSuccessBankInfoSidePanel } from '../success'
import { useReimbursementsInProgress } from '@/features/reimbursements/api/use-reimbursements-inprogress'

type Props = {
  getBankInfo: () => Promise<BankInfoResponse>
  bankInfo?: BankInfoResponse
  banksResponse: AsyncState<BankNameResponse>
}

export type IOption = {
  label: string
  value: string
}

export const ACCOUNT_TYPES: IOption[] = [
  { value: 'CHECKING', label: 'ted.fields.account-type.checking' },
  { value: 'SAVINGS', label: 'ted.fields.account-type.saving' },
]

const initialValues = {
  cpf: '',
  branchCode: '',
  accountNumber: '',
  accountSubType: '',
  bankKey: '',
}

const TedSidePanel: FC<SidePanelElementProps & Props> = ({
  getBankInfo,
  bankInfo,
  banksResponse,
}) => {
  const { t } = useT('reimbursements')
  const sidePanel = useSidePanelContext()
  const schema = getBankInfoSchema(t, 'ted')
  const showSnackbar = useSnackbar()
  const [saveButtonIsLoading, setSaveButtonIsLoading] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues:
      bankInfo?.accountType === 'TED'
        ? {
            cpf: bankInfo?.cpf ?? '',
            branchCode: bankInfo?.branchCode ?? '',
            accountNumber: bankInfo?.accountNumber ?? '',
            accountSubType: bankInfo?.accountSubType ?? '',
            bankKey: bankInfo?.bankKey ?? '',
          }
        : initialValues,
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  })

  const updateBankInfo = useCallback(
    async (data: {
      bankKey: string
      accountSubType: string
      accountNumber: string
      branchCode: string
      cpf: string
    }) => {
      const payload: TedPayload = {
        accountType: 'TED',
        accountSubType: data.accountSubType as BankAccountType,
        accountNumber: data.accountNumber,
        branchCode: data.branchCode,
        cpf: data.cpf,
        bankKey: data.bankKey,
      }
      setSaveButtonIsLoading(true)
      try {
        const res = await postBankInfo(payload, bankInfo?.uuid)
        if (!isNil(res)) {
          sidePanel.onOpen(
            getSuccessBankInfoSidePanel({
              method: 'TED',
            }),
            0,
          )
          getBankInfo()
        }
      } catch (error) {
        // TODO: manage endpoint error codes
        showSnackbar(t('bankinfo.update.error'), {
          variant: 'error',
        })
      } finally {
        setSaveButtonIsLoading(false)
      }
    },
    [bankInfo?.uuid, getBankInfo, showSnackbar, sidePanel, t],
  )

  const { onSubmit } = useReimbursementsInProgress(updateBankInfo)

  return (
    <Stack>
      <Typography
        variant="body1"
        sx={(theme) => ({
          color: theme.palette.v3.content.medium_emphasis,
          marginBottom: theme.spacing(3),
        })}
      >
        {t('ted.settings.description')}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={'column'} gap={3} mb={7}>
          <Controller
            control={control}
            name="cpf"
            render={({ field, fieldState: { error } }) => (
              <PatternField
                {...field}
                label={t(`ted.fields.cpf.label`)}
                variant="outlined"
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                size="small"
                fullWidth
                sx={{
                  marginBottom: 2,
                }}
                inputProps={{
                  format: CpfMask,
                  valueIsNumericString: true,
                  type: 'tel',
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="branchCode"
            render={({ field, fieldState: { error } }) => (
              <NumericField
                {...field}
                fullWidth
                label={t(`ted.fields.branchCode.label`)}
                error={error != null}
                helperText={
                  error
                    ? getErrorHelperText(t, error)
                    : t(`ted.fields.branchCode.helpertext`)
                }
                inputProps={{ maxLength: 4 }}
              />
            )}
          />
          <Controller
            control={control}
            name="accountNumber"
            render={({ field, fieldState: { error } }) => (
              <NumericField
                {...field}
                fullWidth
                label={t(`ted.fields.accountNumber.label`)}
                error={error != null}
                helperText={
                  error
                    ? getErrorHelperText(t, error)
                    : t(`ted.fields.accountNumber.helpertext`)
                }
                inputProps={{ maxLength: 16 }}
              />
            )}
          />
          <Controller
            control={control}
            name="accountSubType"
            render={({ field, fieldState: { error } }) => (
              <Select<IOption, false, GroupBase<IOption>>
                options={ACCOUNT_TYPES}
                label={t('ted.fields.account-type.label')}
                placeholder=""
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                getOptionLabel={(option) => t(option.label)}
                getOptionValue={(option) => option.value}
                value={
                  ACCOUNT_TYPES.find(
                    (accountType) => accountType.value === field.value,
                  ) || null
                }
                onChange={(newValue) => {
                  field.onChange(newValue?.value)
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="bankKey"
            render={({ field, fieldState: { error } }) => (
              <Select<BankName, false, GroupBase<BankName>>
                options={banksResponse.value ?? []}
                label={t('ted.fields.bank.label')}
                placeholder=""
                error={error != null}
                helperText={error && getErrorHelperText(t, error)}
                getOptionLabel={(bank) => bank.name}
                getOptionValue={(bank) => bank.key}
                isLoading={banksResponse.loading}
                value={
                  banksResponse.value?.find(
                    (bank) => bank.key === field.value,
                  ) || null
                }
                onChange={(newValue) => {
                  field.onChange(newValue?.key)
                }}
              />
            )}
          />

          <Stack
            direction="row"
            gap={2}
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(3),
              left: theme.spacing(3),
              right: theme.spacing(3),
              bottom: 0,
              backgroundColor: theme.palette.common.white,
              zIndex: 1,
            })}
          >
            <Button
              type={'submit'}
              sx={{ width: '208px' }}
              isLoading={banksResponse.loading || saveButtonIsLoading}
              isDisabled={isEmpty(banksResponse.value) || !isDirty}
            >
              {t('bankinfo.sidepanel.save')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  )
}

export const getTedSidePanel = ({
  getBankInfo,
  bankInfo,
  banksResponse,
}: Props) =>
  [
    {
      children: [
        {
          element: (
            <TedSidePanel
              getBankInfo={getBankInfo}
              bankInfo={bankInfo}
              banksResponse={banksResponse}
            />
          ),
          header: (
            <Translation ns="reimbursements">
              {(t) => <SidePanelHeader headline={t('ted.settings.title')} />}
            </Translation>
          ),
        },
      ],
    },
  ] as SidePanelObject[]
